/**
 * PageShell + PageHeader — §Phase 3 of the Command Deck redesign.
 *
 * Replaces the copy-pasted page skeleton (`<div className="space-y-6">`
 * → `flex items-center justify-between` with `h1 text-xl` + subtitle +
 * actions) that recurs across ~20 pages. One Command-Deck-styled source
 * of truth. `data-testid` is forwarded so converted pages keep their
 * test hooks; the rendered structure mirrors the old skeleton so
 * conversions are mechanical and behavior-preserving.
 */

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface PageShellProps {
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
}

/** Vertical page rhythm wrapper (was the hand-rolled `space-y-6` div). */
export function PageShell({ children, className, ...rest }: PageShellProps) {
  return (
    <div className={cn("space-y-6", className)} {...rest}>
      {children}
    </div>
  );
}

export interface PageHeaderProps {
  title: ReactNode;
  /** Small muted line under the title (optional). */
  subtitle?: ReactNode;
  /** Right-aligned actions (buttons, filters). */
  actions?: ReactNode;
  className?: string;
  "data-testid"?: string;
}

/**
 * Command Deck page header: title (tight tracking, foreground token) +
 * a thin signal rule, optional muted subtitle, optional right actions.
 * Same DOM shape as the old `flex items-center justify-between` block.
 */
export function PageHeader({
  title,
  subtitle,
  actions,
  className,
  ...rest
}: PageHeaderProps) {
  return (
    <div
      className={cn("flex items-start justify-between gap-4", className)}
      {...rest}
    >
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          {title}
        </h1>
        <div className="mt-1.5 h-px w-10 bg-primary/60" aria-hidden />
        {subtitle != null && (
          <p className="mt-2 text-sm text-muted">{subtitle}</p>
        )}
      </div>
      {actions != null && (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      )}
    </div>
  );
}
